import {
  FORMULA_API_ENDPOINTS,
  FUND_ATTRIBUTE_MAP,
  METRIC_FIELD_MAP,
  COMPANY_ATTRIBUTE_MAP,
  ANSWER_ATTRIBUTE_MAP,
  REPORT_ATTRIBUTE_MAP,
  SCORE_ATTRIBUTE_MAP,
  CATEGORY_ATTRIBUTE_MAP,
  QUESTION_ATTRIBUTE_MAP,
} from "./attributeMaps.js";

import { fetchNovataData } from "./functionUtils.js";

/**
 * Retrieves fund attributes.
 * @customfunction
 * @param {string} fundId Fund identifier.
 * @param {string} fieldName Name of the field to retrieve.
 * @returns {Promise<any>} The field value or error.
 */
async function FUND(fundId, fieldName) {
  return fetchNovataData({
    endpoint: FORMULA_API_ENDPOINTS.FUNDS,
    queryParams: { fundId: fundId },
    requiredParams: { "Fund ID": fundId, "Field name": fieldName },
    attributeMap: FUND_ATTRIBUTE_MAP,
    fieldName: fieldName,
  });
}

/**
 * Retrieves company attributes.
 * @customfunction
 * @param {string} companyId Company identifier.
 * @param {string} fieldName Name of the field to retrieve.
 * @param {string} [fundId] Optional fund identifier.
 * @returns {Promise<any>} The field value or error.
 */
async function COMP(companyId, fieldName, fundId) {
  const requiredParams = { "Company ID": companyId, "Field name": fieldName };

  if (fieldName?.trim() === "company_fund_ownership" && !fundId) {
    return "#ERROR: Fund ID is required for company_fund_ownership.";
  }

  return fetchNovataData({
    endpoint: FORMULA_API_ENDPOINTS.COMPANIES,
    queryParams: { companyId: companyId, fundId: fundId },
    requiredParams,
    attributeMap: COMPANY_ATTRIBUTE_MAP,
    fieldName: fieldName,
  });
}

/**
 * Retrieves metric attributes.
 * @customfunction
 * @param {string} metricId Metric identifier.
 * @param {string} fieldName Name of the field to retrieve.
 * @returns {Promise<any>} The field value or error.
 */
async function METRIC(metricId, fieldName) {
  return fetchNovataData({
    endpoint: FORMULA_API_ENDPOINTS.METRICS,
    queryParams: { metricId: metricId },
    requiredParams: { "Metric ID": metricId, "Field name": fieldName },
    attributeMap: METRIC_FIELD_MAP,
    fieldName: fieldName,
  });
}

/**
 * Retrieves answer/data points.
 * @customfunction
 */
async function ANSWER(companyId, reportingPeriod, report, metricId, fieldName) {
  return fetchNovataData({
    endpoint: FORMULA_API_ENDPOINTS.ANSWERS,
    queryParams: {
      companyId: companyId,
      reportingPeriod: reportingPeriod,
      report: report,
      metricId: metricId,
    },
    requiredParams: {
      "Company ID": companyId,
      "Reporting period": reportingPeriod,
      Report: report,
      "Metric ID": metricId,
      "Field name": fieldName,
    },
    attributeMap: ANSWER_ATTRIBUTE_MAP,
    fieldName: fieldName,
    postProcess: (dataObject, resolvedFieldName) => {
      if (resolvedFieldName !== "answer") return null;
      if (dataObject.metricReported === false) {
        const reason = dataObject.metricNotReportedReason;
        return reason ? `ENR: ${reason}` : "ENR";
      }
      const status = dataObject.reportStatus;
      if (status === "NotStarted") return "Not Started";
      if (status === "InProgress") return "In Progress";
      if (status === "Archived") return "Archived";
      return null;
    },
  });
}

/**
 * Retrieves report attributes.
 * @customfunction
 * @param {string} reportId Report identifier.
 * @param {string} reportName Report name.
 * @param {string} reportingPeriod Reporting period.
 * @param {string} fieldName Name of the field to retrieve.
 * @returns {Promise<any>} The field value or error.
 */
async function REPORT(reportId, reportName, reportingPeriod, fieldName) {
  return fetchNovataData({
    endpoint: FORMULA_API_ENDPOINTS.REPORTS,
    queryParams: {
      reportId: reportId,
      reportName: reportName,
      reportingPeriod: reportingPeriod,
    },
    requiredParams: {
      "Report ID": reportId,
      "Report name": reportName,
      "Reporting period": reportingPeriod,
      "Field name": fieldName,
    },
    attributeMap: REPORT_ATTRIBUTE_MAP,
    fieldName: fieldName,
  });
}

/**
 * Retrieves scorecard totals.
 * @customfunction
 * @param {string} company Company ID or name.
 * @param {string} rubric Rubric ID or name.
 * @param {string} reportingPeriod Reporting period.
 * @param {string} fieldName Name of the field to retrieve.
 * @returns {Promise<any>} The field value or error.
 */
async function SCORE(company, rubric, reportingPeriod, fieldName) {
  return fetchNovataData({
    endpoint: FORMULA_API_ENDPOINTS.SCORECARDS,
    queryParams: { company, rubric, reportingPeriod },
    requiredParams: {
      Company: company,
      Rubric: rubric,
      "Reporting period": reportingPeriod,
      "Field name": fieldName,
    },
    attributeMap: SCORE_ATTRIBUTE_MAP,
    fieldName: fieldName,
  });
}

/**
 * Retrieves a scorecard category.
 * @customfunction
 * @param {string} company Company ID or name.
 * @param {string} rubric Rubric ID or name.
 * @param {string} reportingPeriod Reporting period.
 * @param {string} category Category name.
 * @param {string} fieldName Name of the field to retrieve.
 * @returns {Promise<any>} The field value or error.
 */
async function CATEGORY(company, rubric, reportingPeriod, category, fieldName) {
  return fetchNovataData({
    endpoint: FORMULA_API_ENDPOINTS.SCORECARDS,
    queryParams: { company, rubric, reportingPeriod, category },
    requiredParams: {
      Company: company,
      Rubric: rubric,
      "Reporting period": reportingPeriod,
      Category: category,
      "Field name": fieldName,
    },
    attributeMap: CATEGORY_ATTRIBUTE_MAP,
    fieldName: fieldName,
  });
}

/**
 * Retrieves a scorecard question.
 * @customfunction
 * @param {string} company Company ID or name.
 * @param {string} rubric Rubric ID or name.
 * @param {string} reportingPeriod Reporting period.
 * @param {string} category Category name.
 * @param {string} question Question label.
 * @param {string} fieldName Name of the field to retrieve.
 * @returns {Promise<any>} The field value or error.
 */
async function QUESTION(company, rubric, reportingPeriod, category, question, fieldName) {
  return fetchNovataData({
    endpoint: FORMULA_API_ENDPOINTS.SCORECARDS,
    queryParams: { company, rubric, reportingPeriod, category, question },
    requiredParams: {
      Company: company,
      Rubric: rubric,
      "Reporting period": reportingPeriod,
      Category: category,
      Question: question,
      "Field name": fieldName,
    },
    attributeMap: QUESTION_ATTRIBUTE_MAP,
    fieldName: fieldName,
  });
}

const globalObj =
  typeof self !== "undefined"
    ? self
    : typeof window !== "undefined"
      ? window
      : typeof global !== "undefined"
        ? global
        : this;

globalObj.FUND = FUND;
globalObj.COMP = COMP;
globalObj.METRIC = METRIC;
globalObj.ANSWER = ANSWER;
globalObj.REPORT = REPORT;
globalObj.SCORE = SCORE;
globalObj.CATEGORY = CATEGORY;
globalObj.QUESTION = QUESTION;

if (typeof CustomFunctions !== "undefined" && CustomFunctions.associate) {
  CustomFunctions.associate("FUND", FUND);
  CustomFunctions.associate("COMP", COMP);
  CustomFunctions.associate("METRIC", METRIC);
  CustomFunctions.associate("ANSWER", ANSWER);
  CustomFunctions.associate("REPORT", REPORT);
  CustomFunctions.associate("SCORE", SCORE);
  CustomFunctions.associate("CATEGORY", CATEGORY);
  CustomFunctions.associate("QUESTION", QUESTION);
}
